/**
 * Graph ordering (topological + optional remediation insert) and per-node
 * mastery status + session navigation highlights.
 */

import type { IModuleMasteryData, IConceptState } from '../api/module';
import type { SessionRecommendationNextActionResponse } from '../api/learners';
import type {
  ConceptMasteryStatus,
  ConceptNavHighlight,
  ConceptNode,
  IKnowledgeConcept,
} from './knowledgeConcepts.types';

const VALID_MASTERY: ReadonlySet<string> = new Set([
  'not_assessed',
  'struggling',
  'beginning',
  'developing',
  'confident',
  'mastered',
]);

/**
 * Normalize API `concept_status` to {@link ConceptMasteryStatus}.
 */
export function parseConceptMasteryStatus(
  cs: IConceptState | undefined
): ConceptMasteryStatus {
  const raw = cs?.concept_status;
  if (raw && VALID_MASTERY.has(String(raw))) {
    return raw as ConceptMasteryStatus;
  }
  return 'not_assessed';
}

export function getConceptMasteryStatusForNode(
  nodeId: string,
  moduleMastery: IModuleMasteryData | undefined
): ConceptMasteryStatus {
  const list = moduleMastery?.concept_states ?? [];
  const cs = list.find((c) => c.concept_id === nodeId);
  return parseConceptMasteryStatus(cs);
}

/**
 * Session-driven pill / emphasis only (does not change node fill color).
 */
export function getNavHighlightForGraph(
  nodeId: string,
  session: SessionRecommendationNextActionResponse | undefined
): ConceptNavHighlight | null {
  const nav = session?.navigation;
  const decision = nav?.decision;
  const sessionConceptId = session?.concept_id;
  const nextConceptId = nav?.next_concept_id;
  const isDiagnostic = session?.recommend_diagnostic;

  if (isDiagnostic) {
    return null;
  }

  if (decision === 'not_applicable' && sessionConceptId === nodeId) {
    return 'recommended';
  }

  if (decision === 'promote' || decision === 'return_from_remediation') {
    if (sessionConceptId === nodeId) return 'current';
    if (nextConceptId === nodeId) return 'recommended';
  }

  if (decision === 'remediate' || nav?.is_remediating) {
    if (sessionConceptId === nodeId) return 'struggling';
    if (nextConceptId === nodeId) return 'remediation-target';
  }

  if (decision === 'stay' || decision === 'challenge') {
    if (sessionConceptId === nodeId) return 'recommended';
  }

  return null;
}

/**
 * Topological order from API; may append a synthetic remediation node when
 * `next_concept_id` is not already in the graph.
 */
export function buildOrderedConceptNodes(
  hasData: boolean,
  knowledgeConcepts: IKnowledgeConcept,
  session: SessionRecommendationNextActionResponse | undefined,
  externalModuleId: string | undefined
): ConceptNode[] {
  if (
    !hasData ||
    !knowledgeConcepts.nodes?.length ||
    !knowledgeConcepts.topological_order?.length
  ) {
    return [];
  }

  const nodeMap = new Map(knowledgeConcepts.nodes.map((n) => [n.id, n]));
  const baseOrdered = knowledgeConcepts.topological_order
    .map((id) => nodeMap.get(id))
    .filter(Boolean) as ConceptNode[];

  const nav = session?.navigation;
  const isRemediating =
    nav?.decision === 'remediate' ||
    nav?.decision === 'return_from_remediation' ||
    nav?.is_remediating === true;

  if (!isRemediating) {
    return baseOrdered;
  }

  const remediationStack = (session?.remediation_stack?.stack || []).sort(
    (a, b) => new Date(b.pushed_at).getTime() - new Date(a.pushed_at).getTime()
  );
  const remediationNodeLinkIndex = remediationStack.findIndex((val) =>
    baseOrdered.find((node) => node.id === val.concept_id)
  );
  const remdiationIdsToInsert = remediationStack
    .filter((val) => !baseOrdered.find((node) => node.id === val.concept_id))
    .map((val) => val.concept_id);
  const remdiationIdsToInsertNodes = remdiationIdsToInsert
    .map((id) => nodeMap.get(id))
    .filter(Boolean) as ConceptNode[];
  const baseOrderInsertIdx =
    remediationNodeLinkIndex >= 0
      ? baseOrdered.findIndex(
          (n) => n.id === remediationStack[remediationNodeLinkIndex].concept_id
        )
      : -1;
  const updatedOrdered = [
    ...baseOrdered.slice(0, baseOrderInsertIdx),
    ...remdiationIdsToInsertNodes,
    ...baseOrdered.slice(baseOrderInsertIdx),
  ];
  let updatedIdx = baseOrderInsertIdx - remdiationIdsToInsertNodes.length;
  if (updatedIdx < 0) {
    updatedIdx = 0;
  }

  const navCurrentId = session?.concept_id;
  const navCurrentNode =
    navCurrentId && !updatedOrdered.find((n) => n.id === navCurrentId)
      ? nodeMap.get(navCurrentId)
      : undefined;
  const navNextId = session?.navigation?.next_concept_id;
  const navNextNode = navNextId
    ? !updatedOrdered.find((n) => n.id === navNextId)
      ? nodeMap.get(navNextId) ||
        ({
          id: navNextId,
          external_id: '',
          name: nav?.next_concept_name,
          title: nav?.next_concept_title,
          emoji: nav?.next_concept_emoji || '🎯',
          module_id: nav?.next_concept_module_id || externalModuleId || '',
          module_name: nav?.next_concept_module_name || '',
          section_id: '',
          is_cross_module:
            nav?.is_cross_module_remediation ||
            updatedOrdered.find((n) => n.id === session?.concept_id)
              ?.is_cross_module,
          remediation_depth: nav?.remediation_depth,
        } as ConceptNode)
      : undefined
    : undefined;

  const orderedInWhichToInsertNodes = (
    nav?.decision === 'return_from_remediation'
      ? [navCurrentNode, navNextNode]
      : [navNextNode, navCurrentNode]
  ).filter((val) => val !== undefined) as ConceptNode[];

  return [
    ...updatedOrdered.slice(0, updatedIdx),
    ...orderedInWhichToInsertNodes,
    ...updatedOrdered.slice(updatedIdx),
  ];
}

// export function buildOrderedConceptNodes(
//   hasData: boolean,
//   knowledgeConcepts: IKnowledgeConcept,
//   session: SessionRecommendationNextActionResponse | undefined,
//   externalModuleId: string | undefined
// ): ConceptNode[] {
//   if (
//     !hasData ||
//     !knowledgeConcepts.nodes?.length ||
//     !knowledgeConcepts.topological_order?.length
//   ) {
//     return [];
//   }

//   const nodeMap = new Map(knowledgeConcepts.nodes.map((n) => [n.id, n]));
//   const baseOrdered = knowledgeConcepts.topological_order
//     .map((id) => nodeMap.get(id))
//     .filter(Boolean) as ConceptNode[];

//   const nav = session?.navigation;
//   const isRemediating =
//     nav?.decision === 'remediate' || nav?.is_remediating === true;
//   const remediationNextId =
//     isRemediating && nav?.next_concept_id ? nav.next_concept_id : undefined;
//   const remediationCurrentConceptId =
//     isRemediating && session?.concept_id ? session?.concept_id : undefined;

//   if (!remediationNextId) {
//     return baseOrdered;
//   }

//   const currentRemediationConcept =
//     remediationCurrentConceptId &&
//     !baseOrdered.find((n) => n.id === remediationCurrentConceptId)
//       ? nodeMap.get(remediationCurrentConceptId)
//       : undefined;
//   if (
//     currentRemediationConcept &&
//     currentRemediationConcept.module_id !== externalModuleId &&
//     currentRemediationConcept.module_name
//   ) {
//     currentRemediationConcept.meta_from = `From Ch: ${currentRemediationConcept.module_name}`;
//   }

//   const remediationNode =
//     nodeMap.get(remediationNextId) ||
//     (nav?.next_concept_name
//       ? ({
//           id: remediationNextId,
//           external_id: '',
//           name: nav?.next_concept_name,
//           title: nav?.next_concept_title,
//           emoji: nav?.next_concept_emoji || '🎯',
//           module_id: nav?.next_concept_module_id || externalModuleId || '',
//           module_name: nav?.next_concept_module_name || '',
//           section_id: '',
//           is_cross_module:
//             nav?.is_cross_module_remediation ||
//             baseOrdered.find((n) => n.id === session?.concept_id)
//               ?.is_cross_module,
//           remediation_depth: nav?.remediation_depth,
//         } as ConceptNode)
//       : undefined);
//   if (
//     remediationNode &&
//     remediationNode.module_id !== externalModuleId &&
//     remediationNode.module_name
//   ) {
//     remediationNode.meta_from = `From Ch: ${remediationNode.module_name}`;
//   }

//   if (
//     !remediationNode ||
//     baseOrdered.some((n) => n.id === remediationNode.id)
//   ) {
//     return baseOrdered;
//   }

//   const beforeId = session?.concept_id;
//   // Insert remediation node *before* the current session concept.
//   const conceptIdx =
//     beforeId !== undefined
//       ? baseOrdered.findIndex((n) => n.id === beforeId)
//       : -1;
//   const insertIdx = conceptIdx >= 0 ? conceptIdx : 0;

//   return currentRemediationConcept
//     ? nav?.decision === 'return_from_remediation'
//       ? [
//           ...baseOrdered.slice(0, insertIdx),
//           currentRemediationConcept,
//           remediationNode,
//           ...baseOrdered.slice(insertIdx),
//         ]
//       : [
//           ...baseOrdered.slice(0, insertIdx),
//           remediationNode,
//           currentRemediationConcept,
//           ...baseOrdered.slice(insertIdx),
//         ]
//     : [
//         ...baseOrdered.slice(0, insertIdx),
//         remediationNode,
//         ...baseOrdered.slice(insertIdx),
//       ];
// }
